import { useState, useEffect, useReducer } from 'react'
import { useNavigate } from 'react-router-dom'
import { boardPath, ticketPathFromIds } from '@/lib/boardUrls'
import { Notification } from '@/types'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Info, Languages, Pencil, Shield, LogOut, BarChart3, LayoutDashboard, ChevronDown, ChevronRight, Palette, Sun, Moon } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { useThemeStore } from '@/stores/themeStore'
import { api } from '@/api/client'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { resolveAttachmentUrl } from '@/utils/attachments'
import { config } from '@/config'
import { CredentialsDialog } from './CredentialsDialog'
import { NotificationDropdown } from '@/components/notifications/NotificationDropdown'
import { MyTicketsNavLink } from './MyTicketsNavLink'
import {
  i18n,
  isI18nEnabled,
  persistLanguage,
  SUPPORTED_LANGUAGES,
  LANGUAGE_LABELS,
  type SupportedLanguage,
} from '@/i18n'

type HeaderUIState = {
  infoOpen: boolean;
  profileMenuOpen: boolean;
  boardsMenuOpen: boolean;
  mobileBoardsMenuOpen: boolean;
  mobileMenuOpen: boolean;
  mobileLanguageOpen: boolean;
};
type HeaderUIAction = { type: 'SET'; key: keyof HeaderUIState; value: boolean };
function headerUIReducer(state: HeaderUIState, action: HeaderUIAction): HeaderUIState {
  if (action.type === 'SET') {
    return { ...state, [action.key]: action.value };
  }
  return state;
}
const initialHeaderUI: HeaderUIState = {
  infoOpen: false,
  profileMenuOpen: false,
  boardsMenuOpen: false,
  mobileBoardsMenuOpen: false,
  mobileMenuOpen: false,
  mobileLanguageOpen: false,
};

function AvatarImage({
  url,
  alt,
  initials,
}: {
  url: string | null
  alt: string
  initials: string
}) {
  const [avatarError, setAvatarError] = useState(false)
  const showAvatar = url && !avatarError
  return showAvatar ? (
    <img
      src={url}
      alt={alt}
      className="object-cover w-full h-full"
      onError={() => setAvatarError(true)}
    />
  ) : (
    <span className="text-sm font-semibold text-foreground">{initials}</span>
  )
}

type BoardItem = { id: string; title: string; shortName: string; avatar?: string | null }

function HeaderLogo({ logoUrl, appName, onNavigate }: { logoUrl: string; appName: string; onNavigate: () => void }) {
  return (
    <button
      type="button"
      onClick={onNavigate}
      className="flex gap-2 items-center min-w-0 text-lg font-bold cursor-pointer sm:text-xl hover:opacity-80 transition-opacity"
    >
      <img src={logoUrl} alt="" className="ml-2 w-auto h-8 shrink-0" />
      <span className="min-w-0 truncate max-w-[17ch] sm:max-w-none">{appName}</span>
    </button>
  )
}

function BoardsDropdown({
  boards,
  open,
  onOpenChange,
  onSelectBoard,
  compact,
  t,
}: {
  boards: BoardItem[]
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelectBoard: (board: BoardItem) => void
  compact: boolean
  t: (key: string) => string
}) {
  if (boards.length === 0) return null
  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={compact ? 'cursor-pointer hover:bg-accent' : 'gap-1.5 cursor-pointer'}
          aria-label={t('header.myBoards')}
        >
          <LayoutDashboard className={compact ? 'w-5 h-5' : 'w-4 h-4'} />
          {!compact && (
            <>
              <span className="max-w-30 truncate sm:max-w-none">{t('header.myBoards')}</span>
              <ChevronDown className="w-4 h-4 shrink-0 opacity-70" />
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-[min(60vh,400px)] overflow-y-auto p-2 w-64">
        {boards.map((board) => (
          <DropdownMenuItem
            key={board.id}
            onSelect={() => {
              onOpenChange(false)
              onSelectBoard(board)
            }}
            className="flex gap-3 items-center cursor-pointer min-h-14 py-4 text-lg rounded-md sm:min-h-12 sm:py-3 sm:text-base"
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-muted">
              {board.avatar ? (
                <img src={resolveAttachmentUrl(board.avatar)} alt="" className="h-full w-full object-cover" />
              ) : (
                <span className="text-sm font-semibold text-muted-foreground">
                  {(board.shortName || board.title).charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            <span className="min-w-0 truncate">{board.title}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export function ProfileMenuContent({
  onClose,
  navigate,
  setTheme,
  theme,
  user,
  pendingCount,
  showAppInfo,
  openInfoDialog,
  effectiveLng,
  isMobile,
  mobileLanguageOpen,
  onMobileLanguageOpenChange,
  handleLanguageChange,
  logout,
  t,
}: {
  onClose: () => void
  navigate: (path: string) => void
  setTheme: (theme: 'light' | 'dark') => void
  theme: 'light' | 'dark'
  user: { name?: string; role?: string } | null
  pendingCount: number
  showAppInfo: boolean
  openInfoDialog: () => void
  effectiveLng: SupportedLanguage
  isMobile: boolean
  mobileLanguageOpen: boolean
  onMobileLanguageOpenChange: (open: boolean) => void
  handleLanguageChange: (lng: SupportedLanguage) => void
  logout: () => void
  t: (key: string, opts?: Record<string, number>) => string
}) {
  const itemClass = 'flex gap-3 items-center py-4 text-lg rounded-md cursor-pointer min-h-14 sm:py-3 sm:text-base sm:min-h-12'
  return (
    <>
      {user?.role === 'ADMIN' && (
        <DropdownMenuItem onSelect={() => { onClose(); setTimeout(() => navigate('/admin'), 0) }} className={itemClass}>
          <Shield className="w-6 h-6 shrink-0 sm:w-5 sm:h-5" />
          <span className="flex flex-1 gap-2 items-center">
            {t('common.admin')}
            {pendingCount > 0 && (
              <span className="flex justify-center items-center px-1 h-5 text-xs font-semibold text-destructive-foreground bg-destructive rounded-full min-w-5" aria-label={t('header.usersWaitingApproval', { count: pendingCount })}>
                {pendingCount > 99 ? '99+' : pendingCount}
              </span>
            )}
          </span>
        </DropdownMenuItem>
      )}
      <DropdownMenuItem onSelect={() => { onClose(); setTimeout(() => navigate('/gantt'), 0) }} className={itemClass}>
        <BarChart3 className="w-6 h-6 shrink-0 sm:w-5 sm:h-5" />
        {t('admin.gantt.title')}
      </DropdownMenuItem>
      {showAppInfo && (
        <DropdownMenuItem onSelect={() => { openInfoDialog(); onClose() }} className={itemClass}>
          <Info className="w-6 h-6 shrink-0 sm:w-5 sm:h-5" />
          {t('header.appInfo')}
        </DropdownMenuItem>
      )}
      <DropdownMenuSeparator />
      <DropdownMenuItem onSelect={() => { onClose(); setTimeout(() => navigate('/profile'), 0) }} className={itemClass}>
        <Pencil className="w-6 h-6 shrink-0 sm:w-5 sm:h-5" />
        {t('header.editProfile')}
      </DropdownMenuItem>
      <DropdownMenuItem onSelect={() => { setTheme(theme === 'dark' ? 'light' : 'dark'); onClose() }} className={itemClass}>
        {theme === 'dark' ? <Sun className="w-6 h-6 shrink-0 sm:w-5 sm:h-5" /> : <Moon className="w-6 h-6 shrink-0 sm:w-5 sm:h-5" />}
        {theme === 'dark' ? t('appearance.switchToLightMode') : t('appearance.switchToDarkMode')}
      </DropdownMenuItem>
      <DropdownMenuItem onSelect={() => { onClose(); setTimeout(() => navigate('/appearance'), 0) }} className={itemClass}>
        <Palette className="w-6 h-6 shrink-0 sm:w-5 sm:h-5" />
        {t('profile.appearance')}
      </DropdownMenuItem>
      {isI18nEnabled && (
        isMobile ? (
          <Collapsible open={mobileLanguageOpen} onOpenChange={onMobileLanguageOpenChange}>
            <CollapsibleTrigger asChild>
              <button type="button" className={`flex gap-3 items-center w-full ${itemClass} px-2 text-left text-foreground hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground outline-none`}>
                <Languages className="w-6 h-6 shrink-0 sm:w-5 sm:h-5" />
                {t('header.language')}
                <ChevronRight className={`w-5 h-5 shrink-0 ml-auto transition-transform ${mobileLanguageOpen ? 'rotate-90' : ''}`} />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="pl-2 pt-2 pb-1">
                {SUPPORTED_LANGUAGES.map((lng) => (
                  <DropdownMenuItem key={lng} onSelect={() => { handleLanguageChange(lng); onClose() }} className={`cursor-pointer ${itemClass} pl-10 ${effectiveLng === lng ? 'bg-accent text-accent-foreground' : ''}`}>
                    {LANGUAGE_LABELS[lng]}
                  </DropdownMenuItem>
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>
        ) : (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger className={`flex gap-3 items-center ${itemClass}`}>
              <Languages className="w-6 h-6 shrink-0 sm:w-5 sm:h-5" />
              {t('header.language')}
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="p-2 w-56">
              {SUPPORTED_LANGUAGES.map((lng) => (
                <DropdownMenuItem key={lng} onSelect={() => { handleLanguageChange(lng); onClose() }} className={`cursor-pointer ${itemClass} ${effectiveLng === lng ? 'bg-accent text-accent-foreground' : ''}`}>
                  {LANGUAGE_LABELS[lng]}
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )
      )}
      <DropdownMenuItem onSelect={() => { logout(); onClose() }} className={itemClass}>
        <LogOut className="w-6 h-6 shrink-0 sm:w-5 sm:h-5" />
        {t('header.logout')}
      </DropdownMenuItem>
    </>
  )
}

export function Header() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()
  const [ui, dispatchUI] = useReducer(headerUIReducer, initialHeaderUI)
  const {
    infoOpen,
    profileMenuOpen,
    boardsMenuOpen,
    mobileBoardsMenuOpen,
    mobileMenuOpen,
    mobileLanguageOpen,
  } = ui
  const avatarUrl = user?.avatar ? resolveAttachmentUrl(user.avatar) : null
  const initials = user?.name?.charAt(0).toUpperCase() || '?'

  useEffect(() => {
    if (!mobileMenuOpen) dispatchUI({ type: 'SET', key: 'mobileLanguageOpen', value: false })
  }, [mobileMenuOpen])
  const currentLng = i18n.language as SupportedLanguage
  const effectiveLng = SUPPORTED_LANGUAGES.includes(currentLng) ? currentLng : 'en_GB'
  const theme = useThemeStore((state) => state.theme)
  const setTheme = useThemeStore((state) => state.setTheme)

  const { data: appSettings } = useQuery({
    queryKey: ['app-settings'],
    queryFn: async () => {
      const response = await api.get('/app-settings')
      return response.data.data as {
        appName: string | null;
        logoUrl: string | null;
        defaultAppName?: string;
        updatedAt?: string | null;
      }
    },
  })
  const appName = appSettings?.appName ?? appSettings?.defaultAppName ?? config.defaultAppName
  const logoUrl = appSettings?.logoUrl
    ? resolveAttachmentUrl(appSettings.logoUrl) + (appSettings.updatedAt ? `?t=${appSettings.updatedAt}` : '')
    : '/logo.png'

  const { data: pendingUsers } = useQuery({
    queryKey: ['pending-users'],
    queryFn: async () => {
      const response = await api.get('/users/pending')
      return response.data.data.users as { id: string }[]
    },
    enabled: user?.role === 'ADMIN',
  })
  const pendingCount = pendingUsers?.length ?? 0

  const { data: boardsData } = useQuery({
    queryKey: ['boards'],
    queryFn: async () => {
      const response = await api.get('/boards')
      return response.data.data.boards as { id: string; title: string; shortName: string; avatar?: string | null }[]
    },
    enabled: !!user,
  })
  const boards = boardsData ?? []

  const handleLanguageChange = (lng: SupportedLanguage) => {
    i18n.changeLanguage(lng)
    persistLanguage(lng)
  }

  // Show when unset or not explicitly 'false' (restart dev server after changing .env)
  const showAppInfo = String(import.meta.env.VITE_SHOW_APP_INFO ?? '').trim().toLowerCase() !== 'false'
  const openInfoDialog = () => {
    setTimeout(() => dispatchUI({ type: 'SET', key: 'infoOpen', value: true }), 100)
  }

  const handleNotificationClick = (notification: Notification) => {
    if (notification.ticketId && notification.boardId) {
      const path = ticketPathFromIds(notification.boardId, notification.ticketId);
      setTimeout(() => navigate(path), 0);
    }
  }

  const profileTrigger = (
    <span className="inline-flex relative">
      <button
        type="button"
        className="flex gap-2 items-center p-1 text-foreground rounded-md cursor-pointer hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
        aria-label={t('header.profile')}
      >
        <div className="flex overflow-hidden justify-center items-center w-8 h-8 rounded-full border border-border bg-muted">
          <AvatarImage key={avatarUrl ?? 'no-avatar'} url={avatarUrl} alt={user?.name || t('common.userAvatar')} initials={initials} />
        </div>
      </button>
      {user?.role === 'ADMIN' && pendingCount > 0 && (
        <span className="absolute -top-0.5 -right-0.5 flex justify-center items-center min-w-5 h-5 px-1 text-xs font-semibold text-destructive-foreground bg-destructive rounded-full" aria-label={t('header.usersWaitingApproval', { count: pendingCount })}>
          {pendingCount > 99 ? '99+' : pendingCount}
        </span>
      )}
    </span>
  )
  const profileMenuContentProps = {
    onClose: () => {},
    navigate: (path: string) => setTimeout(() => navigate(path), 0),
    setTheme,
    theme,
    user,
    pendingCount,
    showAppInfo,
    openInfoDialog,
    effectiveLng,
    handleLanguageChange,
    logout,
    t,
  }

  return (
    <header className="fixed top-0 right-0 left-0 z-50 text-foreground bg-card border-b border-border shadow-sm sm:static sm:left-auto sm:right-auto sm:z-auto">
      {showAppInfo && <CredentialsDialog open={infoOpen} onOpenChange={(open) => dispatchUI({ type: 'SET', key: 'infoOpen', value: open })} defaultAppName={appSettings?.defaultAppName ?? config.defaultAppName} />}
      <div className="flex justify-between items-center px-4 mx-auto h-14 container-fluid">
        <HeaderLogo logoUrl={logoUrl} appName={appName} onNavigate={() => navigate('/')} />

        <div className="flex gap-5 items-center sm:gap-4">
          <div className="hidden sm:flex gap-4 items-center">
            <BoardsDropdown
              boards={boards}
              open={boardsMenuOpen}
              onOpenChange={(open) => dispatchUI({ type: 'SET', key: 'boardsMenuOpen', value: open })}
              onSelectBoard={(board) => navigate(boardPath(board))}
              compact={false}
              t={t}
            />
            <MyTicketsNavLink compact={false} onNavigate={() => navigate('/tickets')} label={t('header.myTickets')} />
          </div>
          <div className="flex gap-1 items-center sm:hidden">
            <BoardsDropdown
              boards={boards}
              open={mobileBoardsMenuOpen}
              onOpenChange={(open) => dispatchUI({ type: 'SET', key: 'mobileBoardsMenuOpen', value: open })}
              onSelectBoard={(board) => navigate(boardPath(board))}
              compact
              t={t}
            />
            <MyTicketsNavLink compact onNavigate={() => navigate('/tickets')} label={t('header.myTickets')} />
          </div>

          <NotificationDropdown onNotificationClick={handleNotificationClick} />

          <div className="flex gap-1 items-center sm:hidden">
            <DropdownMenu open={mobileMenuOpen} onOpenChange={(open) => dispatchUI({ type: 'SET', key: 'mobileMenuOpen', value: open })}>
              <DropdownMenuTrigger asChild>{profileTrigger}</DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="p-2 w-64">
                <ProfileMenuContent
                  {...profileMenuContentProps}
                  onClose={() => dispatchUI({ type: 'SET', key: 'mobileMenuOpen', value: false })}
                  isMobile
                  mobileLanguageOpen={mobileLanguageOpen}
                  onMobileLanguageOpenChange={(open) => dispatchUI({ type: 'SET', key: 'mobileLanguageOpen', value: open })}
                />
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="hidden sm:flex items-center">
            <DropdownMenu open={profileMenuOpen} onOpenChange={(open) => dispatchUI({ type: 'SET', key: 'profileMenuOpen', value: open })}>
              <DropdownMenuTrigger asChild>{profileTrigger}</DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="p-2 w-64">
                <ProfileMenuContent
                  {...profileMenuContentProps}
                  onClose={() => dispatchUI({ type: 'SET', key: 'profileMenuOpen', value: false })}
                  isMobile={false}
                  mobileLanguageOpen={false}
                  onMobileLanguageOpenChange={() => {}}
                />
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  )
}
